import React, { PureComponent, Component } from 'react'
import { withFormik, Form, Field } from 'formik'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import MaskedInput from 'react-text-mask'

import { Input, Select, Modal, Button, FlexContainer, FlexRow, FlexedItem, Col, StyledInput, Footer } from 'components'
import {isEmpty, creditcardDetector} from "utils";
import { PAYMENT_CARD } from 'containers/constants'

import messages from './messages'

const P = styled.p`
  margin-bottom: 15px;
`
const CreditCardSelector = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 20px auto;
`

const CreditCardImg = styled.img`
  ${props => (props.isCardSelected ? 'filter: grayscale(0%); opacity: 1;' : 'filter: grayscale(100%); opacity: 0.4;')};
  width: auto;
  height: 15px;
`

const cardsList = [
  {
    type: PAYMENT_CARD.visa.label,
    image: `${process.env.SF_HOST_ENV_VAR.trim()}/images/visa.svg`,
  },
  {
    type: PAYMENT_CARD.mastercard.label,
    image: `${process.env.SF_HOST_ENV_VAR.trim()}/images/mc.svg`,
  },
  {
    type: PAYMENT_CARD.amex.label,
    image: `${process.env.SF_HOST_ENV_VAR.trim()}/images/amex.svg`,
  },
]

const RenderCreditcardsList = ({ cardNumber, detectedCreditCard }) =>
  cardsList.map(card => <CreditCardImg key={card.type} src={card.image} alt={card.type} isCardSelected={card.type === detectedCreditCard} />)

export const PaymentForm = props => {
  const {
    values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting,
  } = props

  const detectedCreditCard = creditcardDetector(values[messages.cardNumber.sid].replace(/ /g, ''))

  /* Set the cartType value based off of the card number */
  values[messages.cardType.sid] = detectedCreditCard || ''

  /* Add masking for credit card */
  const cardNumberMasking = cardNumber => {
    const { length } = cardNumber
    let newVal = ''

    if (!length) return cardNumber

    /* removes all characters in the value that aren't a number,
       or in the range from A to Z (uppercase): */
    newVal = cardNumber
      .replace(/ /g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim()
    return newVal
  }

  /* We need to sanitize the cc number before setting its value. As the masking happens after the onChange
    call, we need to clean it up here by removing any extra trailing character/number, then fire the onChange method
   */
  const handleCardNumberChange = ({ target: { value } }, onChange) => {
    const sanitizedNumber = value.replace(/ /g, '')
    const ccNumber = sanitizedNumber.length > 16 ? sanitizedNumber.slice(0, -1) : sanitizedNumber
    setFieldValue(messages.cardNumber.sid, ccNumber)
    onChange(ccNumber)
  }

  const handleExpiryDateChange = ({ target: { value } }, onChange) => {
    const sanitizedDate = value.replace(/\D/g, '')
    const date = sanitizedDate.length > 4 ? sanitizedDate.slice(0, -1) : sanitizedDate
    setFieldValue(messages.expiryDate.sid, date)
    onChange(date)
  }
  const handleKeyDown = (e, field) => {
    const keyCode = Number(e.keyCode)
    if (e.keyCode === 8) {
      e.preventDefault()
      const sanitizedField = e.target.value.replace(/\D/g, '')
      setFieldValue(field, sanitizedField.slice(0, -1))
    }
  }
  return (
      <Form className="height-percent-100 flex-direction-column">
          <FlexContainer>
              <FlexedItem className="styled-scrollbars">
                  <Col>
                      <CreditCardSelector>
                          <RenderCreditcardsList
                              cardNumber={values[messages.cardNumber.sid]}
                              cardsList={cardsList}
                              detectedCreditCard={detectedCreditCard}
                          />
                      </CreditCardSelector>
                      <MaskedInput
                          mask={[
                              /\d/,
                              /\d/,
                              /\d/,
                              /\d/,
                              " ",
                              /\d/,
                              /\d/,
                              /\d/,
                              /\d/,
                              " ",
                              /\d/,
                              /\d/,
                              /\d/,
                              /\d/,
                              " ",
                              /\d/,
                              /\d/,
                              /\d/,
                              /\d/,
                          ]}
                          guide={false}
                          render={(ref, iprops) => (
                              <Input
                                  label={<FormattedMessage {...messages.cardNumber} />}
                                  type="text"
                                  autoComplete="cc-number"
                                  innerRef={ref}
                                  {...iprops}
                                  value={values[messages.cardNumber.sid]}
                                  name={messages.cardNumber.sid}
                                  onKeyDown={(e) => handleKeyDown(e, messages.cardNumber.sid)}
                                  onChange={(e) => handleCardNumberChange(e, iprops.onChange)}
                                  onBlur={handleBlur}
                                  touched={touched[messages.cardNumber.sid]}
                                  errors={errors[messages.cardNumber.sid]}
                                  validateOnChange={false}
                              />
                          )}
                      />
                      <FlexRow>
                          <MaskedInput
                              mask={[/\d/, /\d/, "/", /\d/, /\d/]}
                              render={(ref, iprops) => (
                                  <Input
                                      label={<FormattedMessage {...messages.expiryDate} className="col-50" />}
                                      type="text"
                                      innerRef={ref}
                                      {...iprops}
                                      name={messages.expiryDate.sid}
                                      onKeyDown={(e) => handleKeyDown(e, messages.expiryDate.sid)}
                                      onChange={(e) => handleExpiryDateChange(e, iprops.onChange)}
                                      onBlur={handleBlur}
                                      placeholder={props.intl.formatMessage({id: messages.expiryDatePlaceholder.id})}
                                      value={values[messages.expiryDate.sid]}
                                      touched={touched[messages.expiryDate.sid]}
                                      errors={errors[messages.expiryDate.sid]}
                                      validateOnChange={false}
                                  />
                              )}
                          />
                          <MaskedInput
                              mask={
                                  detectedCreditCard === PAYMENT_CARD.amex.label
                                      ? [/\d/, /\d/, /\d/, /\d/]
                                      : [/\d/, /\d/, /\d/]
                              }
                              guide={false}
                              render={(ref, iprops) => (
                                  <Input
                                      label={<FormattedMessage {...messages.cvv} className="col-50" />}
                                      type="password"
                                      name={messages.cvv.sid}
                                      innerRef={ref}
                                      {...iprops}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values[messages.cvv.sid]}
                                      touched={touched[messages.cvv.sid]}
                                      errors={errors[messages.cvv.sid]}
                                      showMask={false}
                                      placeholder={props.intl.formatMessage({id: messages.cvvPlaceholder.id})}
                                  />
                              )}
                          />
                      </FlexRow>
                  </Col>
              </FlexedItem>
              <Footer>
                  <Button
                      text={<FormattedMessage {...messages.cta} />}
                      type="submit"
                      disabled={isSubmitting || isEmpty(detectedCreditCard) || !isEmpty(errors)}
                      classes="btn btn-filled-bby"
                  />
              </Footer>
          </FlexContainer>
      </Form>
  );
}
PaymentForm.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  isSubmitting: PropTypes.bool,
  intl: PropTypes.object,
  setFieldValue: PropTypes.func,
}



// WEBPACK FOOTER //
// ./src/containers/Cart/Payment/PaymentForm.js